import React, { useCallback, useMemo, useState } from "react";

import { faFileArrowDown, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { Column, Row } from "components/Layout";
import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import { SideLoadedBusinessColumnDefs } from "constants/columnDefinitions/sideLoadedBusiness";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import { useHasExportBusinessesPermission } from "hooks";
import { useHasSaleyardOrDeploymentPermission } from "hooks/useHasPermission";

import {
  openCreateSideLoadedBusinessModal,
  openSideLoadedDataSetsModal,
} from "lib/navigation";

import { downloadSideLoadedDataSetTemplate } from "lib/sideLoadedDataSetTemplate";

import { getIsStaff, selectSideLoadedBusinessesGridList } from "selectors";
import type { GridSideLoadedBusiness } from "selectors/sideLoadedBusinesses";

import {
  getSideLoadedBusinessRowId,
  useSideLoadedBusinessGridContext,
} from "./useSideLoadedBusinessGridContext";

const uploadAction = {
  default: true,
  title: "Upload CSV",
  onClick: () => openSideLoadedDataSetsModal(),
  icon: faUpload,
  dataTour: "uploadSideLoadedDataSet",
};

const downloadTemplateAction = {
  title: "Download Template",
  onClick: () => downloadSideLoadedDataSetTemplate(),
  icon: faFileArrowDown,
  dataTour: "downloadSideLoadedDataSetTemplate",
};

export function SideLoadedBusinessManageTable(): React.JSX.Element {
  const rowData = useSelector(selectSideLoadedBusinessesGridList);
  const hasExportCsvPermissions = useHasExportBusinessesPermission();
  const canAddDataSet = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canAddSideLoadedDataSet,
    SaleyardPermission.canAddSideLoadedDataSet,
  );
  // Staff-only on top of the permission while the feature beds in, so lifting
  // the restriction is just dropping the isStaff term.
  const isStaff = useSelector(getIsStaff);

  const additionalActions = useMemo(
    () =>
      isStaff && canAddDataSet
        ? [uploadAction, downloadTemplateAction]
        : [downloadTemplateAction],
    [isStaff, canAddDataSet],
  );

  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const selectedRecordCount = selectedRecordIds.length;

  const onRowSelectionChange = useCallback(
    // A selected group row arrives here as undefined - it has no record of its own.
    (rows: (GridSideLoadedBusiness | undefined)[]) => {
      const newSelectedRecordIds = rows.filter(Boolean).map(row => row.id);
      if (
        newSelectedRecordIds.length !== selectedRecordCount ||
        !newSelectedRecordIds.every(id => selectedRecordIds.includes(id))
      ) {
        setSelectedRecordIds(newSelectedRecordIds);
      }
    },
    [selectedRecordCount, selectedRecordIds],
  );

  const defaultCsvExportParams = useMemo(
    () => ({
      onlySelected: !!selectedRecordCount,
    }),
    [selectedRecordCount],
  );

  const handleSelectAndCompare = useCallback((recordId: string) => {
    openCreateSideLoadedBusinessModal(undefined, recordId);
  }, []);

  const context = useSideLoadedBusinessGridContext(
    handleSelectAndCompare,
    "Create Business",
  );

  return (
    <Column fullHeight>
      {rowData.length === 0 && (
        <Row paddingVertical={2} paddingHorizontal={2}>
          <SmallText>
            No Legacy Business data has been uploaded yet. Once a data set has
            been uploaded, its businesses will be listed here.
          </SmallText>
        </Row>
      )}
      <AgGridTable
        additionalActions={additionalActions}
        columnDefs={SideLoadedBusinessColumnDefs}
        context={context}
        getRowId={getSideLoadedBusinessRowId}
        rowData={rowData}
        tableName={AgGridTables.SIDELOADED_BUSINESSES_MANAGE}
        hasExportCsvPermissions={hasExportCsvPermissions}
        exportSaleTitle="Legacy Businesses"
        defaultCsvExportParams={defaultCsvExportParams}
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection={RowSelectionMode.MULTIPLE}
        rowsSelectable
      />
    </Column>
  );
}
